import { Card, CardContent, CardHeader, Grid } from "@mui/material"
import InfoBox from "../../../../common/InfoBox"
import { numberPrecision } from "../../../../helpers/UtilityHelper"
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts'
import ChartTooltip from "../../../charts/ChartTooltip"
import ChartEmpty from "../../../charts/ChartEmpty"
import { ZONE_COLORS } from "../../../charts/colors"

const ZONE_META = [
    { key: 'LOCAL', label: 'Local Zone', color: ZONE_COLORS.LOCAL },
    { key: 'METROS', label: 'Metros Zone', color: ZONE_COLORS.METROS },
    { key: 'REGIONAL', label: 'Regional Zone', color: ZONE_COLORS.REGIONAL },
    { key: 'ROI', label: 'ROI Zone', color: ZONE_COLORS.ROI },
    { key: 'SPECIAL', label: 'Special Zone', color: ZONE_COLORS.SPECIAL },
]

export default function ZoneWiseTrends({ data }) {
    const chartData = (data?.lineChartData || []).map((row) => {
        const total = ZONE_META.reduce((s, z) => s + (row[z.key] || 0), 0)
        return { ...row, _stackTotal: total }
    })
    const chartMax = chartData.reduce((m, r) => Math.max(m, r._stackTotal || 0), 0) || 1
    const plotHeight = 360

    return (
        <Card elevation={1}>
            <CardHeader title="Zone Wise Shipments Trends" />
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={2.4}>
                        <InfoBox header="Local Zone" value={`${numberPrecision(data?.localOrderCount * 100 / data.pickedUpOrderCount, 2)} %`} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="Metro Zone" value={`${numberPrecision(data?.metroOrderCount * 100 / data.pickedUpOrderCount, 2)}%`} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="ROI Zone" value={`${numberPrecision(data?.roiOrderCount * 100 / data.pickedUpOrderCount, 2)}%`} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="Special Zone" value={`${numberPrecision(data?.specialOrderCount * 100 / data.pickedUpOrderCount, 2)} %`} />
                    </Grid>
                    <Grid item xs={2.4}>
                        <InfoBox header="Regional Zone" value={`${numberPrecision(data?.regionalOrderCount * 100 / data.pickedUpOrderCount, 2)} %`} />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        {chartData.length === 0 ? <ChartEmpty height={420} /> : (
                        <ResponsiveContainer width="100%" height={420}>
                            <ComposedChart data={chartData} margin={{ top: 30, right: 20, bottom: 5, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
                                <XAxis dataKey="name" fontSize="11px" fontWeight="500" />
                                <YAxis fontSize="11px" fontWeight="500" label={{ value: '# Shipments', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                                <Tooltip content={<ChartTooltip />} />
                                <Legend verticalAlign="top" align="left" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
                                {ZONE_META.map((z, i) => {
                                    const isLast = i === ZONE_META.length - 1
                                    return (
                                        <Area
                                            key={z.key}
                                            dataKey={z.key}
                                            name={z.label}
                                            stackId="zones"
                                            type="monotone"
                                            stroke={z.color}
                                            fill={z.color}
                                            fillOpacity={0.85}
                                            strokeWidth={1}
                                            animationEasing="ease-in-out"
                                            animationDuration={1000}
                                        >
                                            <LabelList
                                                dataKey={z.key}
                                                content={({ x, y, value }) => {
                                                    if (!value || value === 0 || x == null || y == null) return null
                                                    const segPx = (value / chartMax) * plotHeight
                                                    if (segPx < 16) return null
                                                    return (
                                                        <text
                                                            x={x}
                                                            y={y + segPx / 2 + 3}
                                                            fill="rgba(0,0,0,0.55)"
                                                            fontSize={9}
                                                            fontWeight={500}
                                                            textAnchor="middle"
                                                        >
                                                            {value}
                                                        </text>
                                                    )
                                                }}
                                            />
                                            {isLast && (
                                                <LabelList dataKey="_stackTotal" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }} formatter={(v) => (v == null || v === 0) ? '' : v.toLocaleString('en-IN')} />
                                            )}
                                        </Area>
                                    )
                                })}
                            </ComposedChart>
                        </ResponsiveContainer>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
